import { ComponentProps, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { intersection, isEmpty } from 'lodash/fp';

import { Button, FlexRow, SvgIcon } from '@portal/common/components';
import { styled } from '@portal/common/theme';
import { COMPACT_BUTTON_SIZE } from '@portal/common/theme/theme-config';
import { DataGranularityKey, DataValue, SmartOptionConfig } from '@portal/common/types';
import { matchSize } from '@portal/common/utility/common-props-helpers';

import _ from '../../locale';
import { validateSelectedRefinementFiltersComplexityLimit } from '../../store/data-explorer/actions';
import {
    getSelectedConditionsDataTypesGranularity,
    getSelectedConditionsRefinedGranularity,
    getSelectedRefinementFiltersWithFallbackToDefaultFilters,
} from '../../store/data-explorer/selectors';
import { useDataExplorerLayout } from '../DataExplorer/context';
import SmartOptionsDropdown from './SmartOptionsDropdown';

const StyledSmartOptionsDropdown = styled(SmartOptionsDropdown)({
    marginTop: -14,
    marginBottom: -14,
});

const StyledFlexRow = styled(FlexRow)<{ isCompact?: boolean }>(({ isCompact }) => ({
    marginTop: isCompact ? 23 : -8,
}));

const StyledButton = styled(Button)(({ size }) => ({
    minWidth: matchSize({ sm: COMPACT_BUTTON_SIZE, md: 120 })(size),
    padding: '6px 10px',
}));

type Props = ComponentProps<typeof Button> & {
    filterKey: DataGranularityKey;
    selectedConditions: unknown;
    hideDelete?: boolean;
    onChange?: (val: DataValue) => void;
    isSelectAllEnabled?: boolean;
    smartOptions?: SmartOptionConfig[];
};

const SmartOptionsSelector = (componentProps: Props) => {
    const {
        className,
        filterKey,
        isSelectAllEnabled,
        smartOptions = [],
        onChange,
        ...props
    } = componentProps;
    const dispatch = useDispatch();
    const selectedRefinementFilters = useSelector(
        getSelectedRefinementFiltersWithFallbackToDefaultFilters
    );
    const granularity = useSelector(getSelectedConditionsDataTypesGranularity);
    const refinedGranularity = useSelector(getSelectedConditionsRefinedGranularity);
    const { isCompact, size } = useDataExplorerLayout();

    const doSelect = useCallback(
        (value: DataValue | null) => {
            if (value === null) {
                return;
            }

            const refinementFilters = { [filterKey]: value };
            dispatch(validateSelectedRefinementFiltersComplexityLimit(refinementFilters));
            if (onChange) {
                onChange(value);
            }
        },
        [dispatch, filterKey, onChange]
    );

    const handleSmartOptionSelect = useCallback(
        (value: SmartOptionConfig) => {
            const currentValues = selectedRefinementFilters[filterKey];

            let options = [];
            if (refinedGranularity && refinedGranularity[filterKey]) {
                options = refinedGranularity[filterKey];
            } else if (granularity && granularity[filterKey]) {
                options = granularity[filterKey];
            }

            const newValue = intersection(
                options,
                value.getNewSelectedValues(currentValues, options) || []
            );

            if (!isEmpty(newValue)) {
                doSelect(newValue);
            }
        },
        [doSelect, filterKey, granularity, refinedGranularity, selectedRefinementFilters]
    );

    const handleSelectAllClick = useCallback(() => {
        if (refinedGranularity && refinedGranularity[filterKey]) {
            doSelect(refinedGranularity[filterKey] || []);
        }
    }, [doSelect, filterKey, refinedGranularity]);

    const label = _(isSelectAllEnabled ? 'data_explorer_select_all' : 'data_explorer_select_more');

    return (
        <StyledSmartOptionsDropdown
            placement="bottom"
            smartOptions={smartOptions}
            onSmartOptionSelect={handleSmartOptionSelect}
        >
            {({ ref, toggleVisibility }) => (
                <StyledFlexRow className={className} isCompact={isCompact}>
                    {isSelectAllEnabled ? (
                        <StyledButton
                            onClick={handleSelectAllClick}
                            color="primary"
                            size={size}
                            aria-label={label}
                            {...props}
                        >
                            {isCompact ? (
                                <SvgIcon src="/icons/ic-select-all.svg" width={20} height={20} />
                            ) : (
                                label
                            )}
                        </StyledButton>
                    ) : (
                        <StyledButton
                            ref={ref}
                            color="primary"
                            disabled={isEmpty(smartOptions)}
                            onClick={toggleVisibility}
                            size={size}
                            aria-label={label}
                            {...props}
                        >
                            {isCompact ? (
                                <SvgIcon src="/icons/ic-list.svg" width={20} height={20} />
                            ) : (
                                label
                            )}
                        </StyledButton>
                    )}
                </StyledFlexRow>
            )}
        </StyledSmartOptionsDropdown>
    );
};

export default SmartOptionsSelector;
